import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { ArrowUpDown, Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

// Per-dispatcher workload/performance -- loads created (dispatched) and
// revenue generated in the period, attributed via Load.created_by.
// Filtered on the load's created date (when it was dispatched), not
// pickup/delivery, since this measures dispatcher activity/output.
export default function DispatcherManagementReport() {
  const [loads, setLoads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('Total Revenue');
  const [sortDir, setSortDir] = useState(-1);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Load.list('-created_date', 1000),
      client.entities.StaffProfile.list('-created_date', 200),
    ]).then(([l, st]) => { setLoads(l); setStaff(st); setLoading(false); });
  }, []);

  const staffNameByUserId = new Map(staff.map(s => [s.user_id, s.full_name || s.user_email]));

  const filtered = loads.filter(l => inRange(l.created_date, start, end) && l.created_by);

  const byDispatcher = {};
  filtered.forEach(l => {
    const name = staffNameByUserId.get(l.created_by) || 'Unknown';
    if (!byDispatcher[name]) byDispatcher[name] = [];
    byDispatcher[name].push(l);
  });

  let rows = Object.entries(byDispatcher).map(([dispatcher, list]) => {
    const revenue = list.reduce((s, l) => s + (l.rate || 0), 0);
    const delivered = list.filter(l => ['Delivered', 'Invoiced'].includes(l.status)).length;
    return {
      Dispatcher: dispatcher,
      'Loads Created': list.length,
      Delivered: delivered,
      'Total Revenue': revenue,
      'Avg Rate/Load': list.length > 0 ? revenue / list.length : 0,
    };
  });

  rows.sort((a, b) => sortDir * ((b[sortKey] ?? 0) - (a[sortKey] ?? 0)));

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const grandLoads = rows.reduce((s, r) => s + r['Loads Created'], 0);
  const grandRevenue = rows.reduce((s, r) => s + r['Total Revenue'], 0);

  const csvRows = rows.map(r => ({ ...r, 'Total Revenue': r['Total Revenue'].toFixed(2), 'Avg Rate/Load': r['Avg Rate/Load'].toFixed(2) }));

  const handlePrint = () => {
    const cols = ['Dispatcher', 'Loads Created', 'Delivered', 'Total Revenue', 'Avg Rate/Load'];
    const dataRows = rows.map(r => [
      { value: r.Dispatcher, class: 'bold' }, r['Loads Created'], r.Delivered,
      { value: `$${r['Total Revenue'].toLocaleString()}`, class: 'positive' },
      { value: `$${r['Avg Rate/Load'].toFixed(2)}`, class: 'gold' },
    ]);
    const totalsRow = ['TOTALS', grandLoads, rows.reduce((s, r) => s + r.Delivered, 0), { value: `$${grandRevenue.toLocaleString()}`, class: 'gold' }, ''];
    const cards = [
      { label: 'Dispatchers', value: rows.length, color: 'gold' },
      { label: 'Loads Dispatched', value: grandLoads, color: '' },
      { label: 'Revenue Generated', value: `$${grandRevenue.toLocaleString()}`, color: 'positive' },
    ];
    const html = generatePrintHTML('Dispatcher Management Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'dispatcher-management.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Dispatcher', 'Loads Created', 'Delivered', 'Total Revenue', 'Avg Rate/Load'].map(h => (
                <th key={h} onClick={() => toggleSort(h)} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap cursor-pointer hover:text-foreground select-none">
                  <span className="flex items-center gap-1">{h} <ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.Dispatcher} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-foreground">{r.Dispatcher}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Loads Created']}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.Delivered}</td>
                <td className="px-3 py-2.5 font-semibold text-green-400">${r['Total Revenue'].toLocaleString()}</td>
                <td className="px-3 py-2.5 text-primary">${r['Avg Rate/Load'].toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td className="px-3 py-2.5 text-foreground">TOTALS</td>
                <td className="px-3 py-2.5 text-foreground">{grandLoads}</td>
                <td className="px-3 py-2.5 text-foreground">{rows.reduce((s, r) => s + r.Delivered, 0)}</td>
                <td className="px-3 py-2.5 text-foreground">${grandRevenue.toLocaleString()}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No dispatcher activity for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}
